import { useState, useRef, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Country = {
  id: number
  code: string
  name: string
  currency_code: string
  timezone: string
  is_active: boolean
  default_vat_rate: string | null
}

const ACTIVE_COLOR: Record<'ACTIVE' | 'INACTIVE', string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  INACTIVE: 'bg-gray-100 text-gray-600',
}

type TabKey = 'holidays' | 'leaves' | 'contracts' | 'night' | 'documents' | 'isco'

// ─────────────────── Page ───────────────────
export default function CountryDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const countryId = Number(id)
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()
  const [tab, setTab] = useState<TabKey>('holidays')

  const countryQ = useQuery({
    queryKey: ['country', countryId],
    queryFn: async () => (await api.get<Country>(`/countries/${countryId}/`)).data,
    enabled: !!countryId,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Country>) =>
      (await api.patch(`/countries/${countryId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['country', countryId] })
      qc.invalidateQueries({ queryKey: ['countries'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/countries/${countryId}/`),
    onSuccess: () => {
      toast.success('Country deleted')
      qc.invalidateQueries({ queryKey: ['countries'] })
      navigate('/admin/countries')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  if (countryQ.isLoading) return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  if (countryQ.isError || !countryQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Country not found.</p>
          <Link to="/admin/countries" className="text-blue-600 hover:underline">← Back to countries</Link>
        </div>
      </AdminLayout>
    )
  }

  const country = countryQ.data

  const save = (payload: Partial<Country>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/countries" className="text-muted-foreground hover:text-foreground hover:underline">← Countries</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{country.code}</div>
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <InlineText value={country.name} onSave={(v) => save({ name: v })} />
              <Badge variant="secondary" className="font-mono">{country.currency_code}</Badge>
            </h2>
            <div className="text-sm text-muted-foreground">
              Timezone: <InlineText value={country.timezone} onSave={(v) => save({ timezone: v })} placeholder="e.g. Europe/Berlin" />
              {' · '}
              Default VAT:{' '}
              <InlineNumber
                value={country.default_vat_rate || ''}
                step="0.01"
                onSave={(v) => save({ default_vat_rate: v === '' ? null : v } as Partial<Country>)}
                displayFormat={(v) => v
                  ? <span className="font-mono">{Number(v).toFixed(2)}%</span>
                  : <Dash />}
              />
              {' · '}
              Currency:{' '}
              <InlineText
                value={country.currency_code}
                onSave={(v) => save({ currency_code: v.toUpperCase().slice(0, 3) })}
                className="font-mono"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={country.is_active ? 'ACTIVE' : 'INACTIVE'}
              colorMap={ACTIVE_COLOR}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
              onSave={(v) => save({ is_active: v === 'ACTIVE' })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(
                    `country ${country.code} (${country.name})`,
                    () => deleteMut.mutate(),
                  )}
                  className="text-destructive"
                >
                  Delete country
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="border-b mb-6 overflow-x-auto">
        <nav className="flex gap-1 text-sm">
          {([
            { v: 'holidays' as TabKey, label: 'Public Holidays' },
            { v: 'leaves' as TabKey, label: 'Leave Types' },
            { v: 'contracts' as TabKey, label: 'Contract Types' },
            { v: 'night' as TabKey, label: 'Night Hours' },
            { v: 'documents' as TabKey, label: 'Documents' },
            { v: 'isco' as TabKey, label: 'ISCO-08' },
          ]).map((t) => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`px-4 py-2 -mb-px border-b-2 whitespace-nowrap ${
                tab === t.v ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'holidays' && <HolidaysTab countryId={countryId} />}
      {tab === 'leaves' && <LeavesTab countryId={countryId} />}
      {tab === 'contracts' && <ContractsTab countryId={countryId} />}
      {tab === 'night' && <NightHoursTab countryId={countryId} />}
      {tab === 'documents' && <DocumentsTab countryId={countryId} />}
      {tab === 'isco' && <ISCOTab countryId={countryId} countryCode={countryQ.data?.code || ''} />}
    </AdminLayout>
  )
}

// ──────────────────────── Generic CRUD section ────────────────────────
// Renders a sub-collection table with a "+ Add" RecordDrawer and per-row
// Edit/Delete via a kebab. Same shape as other Record Page sub-collections.

type FieldDef = {
  key: string
  label: string
  type?: 'text' | 'number' | 'date' | 'time' | 'checkbox' | 'select' | 'textarea'
  options?: { value: string; label: string }[]
  placeholder?: string
  helper?: string
  /** Disable this field's input when the predicate returns true (cosmetic;
   *  the backend should still enforce any underlying invariant). */
  disabledWhen?: (draft: Record<string, any>) => boolean
  /** When this field's value changes, return an object of additional
   *  draft updates to apply (e.g. forcing a dependent field to false). */
  cascadeWhenChanged?: (newValue: any, draft: Record<string, any>) => Record<string, any> | null
  /** Optional override of `helper` text when the field is disabled. */
  helperWhenDisabled?: string
}

function CRUDSection<T extends { id: number }>({
  title, description, endpoint, countryId, fields, columns,
  defaultValues, deleteConfirmField = 'name',
}: {
  title: string
  description?: string
  endpoint: string
  countryId: number
  fields: FieldDef[]
  columns: { key: string; label: string; render?: (row: T) => any; className?: string }[]
  defaultValues: Record<string, any>
  deleteConfirmField?: string
}) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const queryKey = [endpoint, countryId]

  const listQ = useQuery({
    queryKey,
    queryFn: async () => (await api.get<T[]>(`/${endpoint}/`, { params: { country: countryId } })).data,
  })

  const [editing, setEditing] = useState<T | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<Record<string, any>>({})

  function openCreate() {
    setEditing(null)
    setDraft({ ...defaultValues })
    setCreating(true)
  }
  function openEdit(row: T) {
    setCreating(false)
    setEditing(row)
    setDraft({ ...(row as any) })
  }
  function closeDrawer() { setCreating(false); setEditing(null) }

  const createMut = useMutation({
    mutationFn: async () => (await api.post(`/${endpoint}/`, { ...draft, country: countryId })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey })
      toast.success(`${title} added`); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const updateMut = useMutation({
    mutationFn: async () => (await api.patch(`/${endpoint}/${(editing as any)!.id}/`, draft)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey })
      toast.success(`${title} saved`); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/${endpoint}/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey })
      toast.success(`${title} deleted`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const rows = listQ.data || []

  return (
    <section className="bg-card rounded-lg shadow">
      {dialog}
      <div className="px-6 py-3 border-b flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        </div>
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add</Button>
      </div>

      <div className="p-6">
        {listQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No entries yet.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                {columns.map((c) => (
                  <Th key={c.key} className={c.className}>{c.label}</Th>
                ))}
                <Th className="text-right w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-muted/30">
                  {columns.map((c) => (
                    <Td key={c.key} className={c.className}>
                      {c.render ? c.render(row) : ((row as any)[c.key] ?? <Dash />)}
                    </Td>
                  ))}
                  <Td className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(row)}>Edit…</DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(
                            `"${(row as any)[deleteConfirmField] || 'this entry'}"`,
                            () => deleteMut.mutate(row.id),
                          )}
                          className="text-destructive"
                        >Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <RecordDrawer
        open={creating || editing !== null}
        onOpenChange={(v) => { if (!v) closeDrawer() }}
        title={editing ? `Edit ${title}` : `Add ${title}`}
        description={editing ? `Update this ${title.toLowerCase()}.` : `Add a new ${title.toLowerCase()} for this country.`}
        onSubmit={() => editing ? updateMut.mutate() : createMut.mutate()}
        isPending={createMut.isPending || updateMut.isPending}
        submitLabel={editing ? 'Save' : 'Add'}
      >
        <FieldGroup>
          {fields.map((f) => (
            <DynamicField
              key={f.key}
              def={f}
              value={draft[f.key]}
              draft={draft}
              onChange={(v) => setDraft((d) => {
                const cascade = f.cascadeWhenChanged ? (f.cascadeWhenChanged(v, d) || {}) : {}
                return { ...d, [f.key]: v, ...cascade }
              })}
            />
          ))}
        </FieldGroup>
      </RecordDrawer>
    </section>
  )
}

function DynamicField({
  def, value, draft, onChange,
}: { def: FieldDef; value: any; draft: Record<string, any>; onChange: (v: any) => void }) {
  const disabled = def.disabledWhen ? def.disabledWhen(draft) : false
  const helperText = disabled && def.helperWhenDisabled ? def.helperWhenDisabled : def.helper

  if (def.type === 'checkbox') {
    return (
      <Field>
        <label className={`flex items-center gap-2 text-sm ${disabled ? 'opacity-50' : ''}`}>
          <input type="checkbox" checked={!!value} disabled={disabled}
            onChange={(e) => onChange(e.target.checked)} />
          {def.label}
        </label>
        {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
      </Field>
    )
  }
  if (def.type === 'select') {
    return (
      <Field>
        <FieldLabel htmlFor={def.key}>{def.label}</FieldLabel>
        <Select
          value={value !== undefined && value !== null ? String(value) : undefined}
          onValueChange={onChange}
          disabled={disabled}
        >
          <SelectTrigger id={def.key}><SelectValue /></SelectTrigger>
          <SelectContent>
            {def.options?.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
      </Field>
    )
  }
  if (def.type === 'textarea') {
    return (
      <Field>
        <FieldLabel htmlFor={def.key}>{def.label}</FieldLabel>
        <Textarea id={def.key} value={value ?? ''} disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          rows={3} placeholder={def.placeholder} />
        {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
      </Field>
    )
  }
  return (
    <Field>
      <FieldLabel htmlFor={def.key}>{def.label}</FieldLabel>
      <Input id={def.key} type={def.type || 'text'} value={value ?? ''} disabled={disabled}
        onChange={(e) => onChange(
          def.type === 'number'
            ? (e.target.value === '' ? null : Number(e.target.value))
            : e.target.value,
        )}
        placeholder={def.placeholder} />
      {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
    </Field>
  )
}

// ──────────────────────── Tab implementations ────────────────────────

type PublicHoliday = {
  id: number
  country: number
  name: string
  date: string
  holiday_type: 'FIXED' | 'VARIABLE'
  is_paid: boolean
  notes: string
}

const HOLIDAY_YEARS = (() => {
  const out: number[] = []
  for (let y = 2050; y >= 2000; y--) out.push(y)
  return out
})()

function HolidaysTab({ countryId }: { countryId: number }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const [year, setYear] = useState(new Date().getFullYear())
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<PublicHoliday | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<Partial<PublicHoliday>>({})

  function openCreate() {
    setEditing(null)
    setDraft({ name: '', date: `${year}-01-01`, holiday_type: 'FIXED', is_paid: true, notes: '' })
    setCreating(true)
  }
  function openEdit(h: PublicHoliday) {
    setCreating(false); setEditing(h); setDraft({ ...h })
  }
  function closeDrawer() { setCreating(false); setEditing(null) }

  const queryKey = ['public-holidays', countryId, year, search]
  const listQ = useQuery({
    queryKey,
    queryFn: async () => (await api.get<PublicHoliday[]>('/public-holidays/', {
      params: { country: countryId, year, ...(search ? { search } : {}) },
    })).data,
  })

  const createMut = useMutation({
    mutationFn: async () => (await api.post('/public-holidays/', {
      ...draft, country: countryId,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['public-holidays', countryId] })
      toast.success('Public holiday added'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const updateMut = useMutation({
    mutationFn: async () => (await api.patch(`/public-holidays/${editing!.id}/`, draft)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['public-holidays', countryId] })
      toast.success('Public holiday saved'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/public-holidays/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['public-holidays', countryId] })
      toast.success('Public holiday deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const rows = listQ.data || []

  return (
    <section className="bg-card rounded-lg shadow">
      {dialog}
      <div className="px-6 py-3 border-b">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-foreground">Public Holidays</h3>
          <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add holiday</Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Input
            type="text" placeholder="Search by name…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-64"
          />
          <SearchableSelect
            className="w-28"
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
            options={HOLIDAY_YEARS.map((y) => ({ value: String(y), label: String(y) }))}
          />
          <span className="text-xs text-muted-foreground">
            {rows.length} holiday{rows.length === 1 ? '' : 's'} in {year}
          </span>
        </div>
      </div>

      <div className="p-6">
        {listQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center border border-dashed rounded-md">
            No public holidays for {year}. Add one above.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Name</Th>
                <Th className="w-40">Date</Th>
                <Th className="w-28">Type</Th>
                <Th className="w-20">Paid</Th>
                <Th className="text-right w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((h) => (
                <tr key={h.id} className="hover:bg-muted/30">
                  <Td className="font-medium">{h.name}</Td>
                  <Td className="font-mono text-xs">
                    {new Date(h.date).toLocaleDateString(undefined, {
                      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
                    })}
                  </Td>
                  <Td>
                    <Badge variant={h.holiday_type === 'FIXED' ? 'secondary' : 'outline'}>
                      {h.holiday_type === 'FIXED' ? 'Fixed' : 'Variable'}
                    </Badge>
                  </Td>
                  <Td className="text-xs">{h.is_paid ? 'Yes' : 'No'}</Td>
                  <Td className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(h)}>Edit…</DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(
                            `holiday "${h.name}"`,
                            () => deleteMut.mutate(h.id),
                          )}
                          className="text-destructive"
                        >Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <RecordDrawer
        open={creating || editing !== null}
        onOpenChange={(v) => { if (!v) closeDrawer() }}
        title={editing ? 'Edit public holiday' : 'Add public holiday'}
        description={editing ? 'Update this holiday.' : 'Add a public holiday for this country.'}
        onSubmit={() => editing ? updateMut.mutate() : createMut.mutate()}
        isPending={createMut.isPending || updateMut.isPending}
        submitLabel={editing ? 'Save' : 'Add holiday'}
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="ph-name">Name *</FieldLabel>
            <Input id="ph-name" autoFocus required value={draft.name || ''}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="ph-date">Date *</FieldLabel>
            <DatePicker id="ph-date" required value={draft.date || ''}
              onChange={(v) => setDraft({ ...draft, date: v })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="ph-type">Type</FieldLabel>
            <Select value={draft.holiday_type || 'FIXED'}
              onValueChange={(v) => setDraft({ ...draft, holiday_type: v as PublicHoliday['holiday_type'] })}>
              <SelectTrigger id="ph-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="FIXED">Fixed (same date every year)</SelectItem>
                <SelectItem value="VARIABLE">Variable (e.g. Easter)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!draft.is_paid}
                onChange={(e) => setDraft({ ...draft, is_paid: e.target.checked })} />
              Paid public holiday
            </label>
          </Field>
          <Field>
            <FieldLabel htmlFor="ph-notes">Notes</FieldLabel>
            <Textarea id="ph-notes" rows={2} value={draft.notes || ''}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </section>
  )
}

function LeavesTab({ countryId }: { countryId: number }) {
  return (
    <CRUDSection
      title="Leave Type"
      description="Statutory and company leave types available in this country. HR-only types are hidden from employee self-service forms; their approval is implicit (HR registering the record IS the approval)."
      endpoint="leave-types"
      countryId={countryId}
      defaultValues={{
        code: '', name: '', default_days_per_year: 0,
        is_paid: true, is_hr_only: false, requires_approval: true,
        max_carryover_days: null, notes: '',
      }}
      fields={[
        { key: 'code', label: 'Code', placeholder: 'VACATION, SICK, PARENTAL…' },
        { key: 'name', label: 'Name' },
        { key: 'default_days_per_year', label: 'Default days per year', type: 'number' },
        { key: 'is_paid', label: 'Paid leave', type: 'checkbox' },
        {
          key: 'is_hr_only', label: 'HR-only (employee cannot self-request)', type: 'checkbox',
          helper: 'Tick for leave types HR registers post-hoc (e.g. Absence, Sick leave with a doctor\'s note). Approval is implicit.',
          // When HR-only flips ON, force requires_approval off — HR registering
          // the record IS the approval.
          cascadeWhenChanged: (newValue) => newValue ? { requires_approval: false } : null,
        },
        {
          key: 'requires_approval', label: 'Requires approval', type: 'checkbox',
          disabledWhen: (draft) => !!draft.is_hr_only,
          helperWhenDisabled: 'Disabled — HR-only types don\'t need separate approval.',
        },
        { key: 'max_carryover_days', label: 'Max carry-over days (blank = unlimited)', type: 'number' },
        { key: 'notes', label: 'Notes', type: 'textarea' },
      ]}
      columns={[
        { key: 'code', label: 'Code', className: 'font-mono' },
        { key: 'name', label: 'Name' },
        { key: 'default_days_per_year', label: 'Days/yr', className: 'text-right' },
        { key: 'is_paid', label: 'Paid', render: (r: any) => r.is_paid ? 'Yes' : 'No' },
        {
          key: 'is_hr_only', label: 'Visibility',
          render: (r: any) => r.is_hr_only
            ? <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-amber-100 text-amber-800">HR-only</span>
            : <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-green-100 text-green-800">Employee-requestable</span>,
        },
        {
          key: 'requires_approval', label: 'Approval',
          render: (r: any) => r.is_hr_only
            ? <span className="text-xs text-muted-foreground">implicit</span>
            : (r.requires_approval ? 'Required' : 'Auto-approve'),
        },
      ]}
    />
  )
}

function ContractsTab({ countryId }: { countryId: number }) {
  return (
    <CRUDSection
      title="Labour Contract Type"
      description="Statutory employment contract types defined for this country."
      endpoint="contract-types"
      countryId={countryId}
      defaultValues={{ code: '', name: '', is_fixed_term: false, max_duration_months: null, probation_period_days: 0, notice_period_days: 0, annual_leave_days: null, public_holidays_per_year: null, description: '' }}
      fields={[
        { key: 'code', label: 'Code', placeholder: 'CDI, CDD, FULL_TIME…' },
        { key: 'name', label: 'Name' },
        { key: 'is_fixed_term', label: 'Fixed-term contract', type: 'checkbox' },
        { key: 'max_duration_months', label: 'Max duration (months, fixed-term)', type: 'number' },
        { key: 'probation_period_days', label: 'Probation period (days)', type: 'number' },
        { key: 'notice_period_days', label: 'Notice period (days)', type: 'number' },
        { key: 'annual_leave_days', label: 'Annual leave entitlement (days)', type: 'number' },
        { key: 'public_holidays_per_year', label: 'Public holidays per year', type: 'number' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]}
      columns={[
        { key: 'code', label: 'Code', className: 'font-mono' },
        { key: 'name', label: 'Name' },
        { key: 'is_fixed_term', label: 'Term', render: (r: any) => r.is_fixed_term ? `Fixed ${r.max_duration_months || '?'}mo` : 'Open-ended' },
        { key: 'notice', label: 'Notice', render: (r: any) => `${r.notice_period_days}d` },
        { key: 'annual_leave_days', label: 'Leave', render: (r: any) => r.annual_leave_days != null ? `${r.annual_leave_days}d` : '—' },
      ]}
    />
  )
}

function DocumentsTab({ countryId }: { countryId: number }) {
  return (
    <CRUDSection
      title="Standard Document"
      description="Identity / tax / employment documents recognised in this country."
      endpoint="standard-documents"
      countryId={countryId}
      defaultValues={{ code: '', name: '', required_for_employment: false, required_for_invoicing: false, issuing_authority: '', validation_pattern: '', description: '' }}
      fields={[
        { key: 'code', label: 'Code', placeholder: 'NIF, NIN, VAT, PASSPORT…' },
        { key: 'name', label: 'Name' },
        { key: 'required_for_employment', label: 'Required to employ', type: 'checkbox' },
        { key: 'required_for_invoicing', label: 'Required to invoice', type: 'checkbox' },
        { key: 'issuing_authority', label: 'Issuing authority' },
        { key: 'validation_pattern', label: 'Validation regex (optional)' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]}
      columns={[
        { key: 'code', label: 'Code', className: 'font-mono' },
        { key: 'name', label: 'Name' },
        { key: 'employment', label: 'For employment', render: (r: any) => r.required_for_employment ? '✓' : '—' },
        { key: 'invoicing', label: 'For invoicing', render: (r: any) => r.required_for_invoicing ? '✓' : '—' },
      ]}
    />
  )
}

function ISCOTab({ countryId, countryCode }: { countryId: number; countryCode: string }) {
  const qc = useQueryClient()
  const isRomania = countryCode === 'RO'
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const onImportSuccess = (res: any) => {
    const created = res?.created ?? 0
    const updated = res?.updated ?? 0
    const ug = res?.unit_groups_touched ?? 0
    toast.success(
      `COR import complete — ${created} created, ${updated} updated, ${ug} ISCO-08 unit groups touched.`,
    )
    qc.invalidateQueries({ queryKey: ['isco-codes', countryId] })
  }

  const importUrlMut = useMutation({
    mutationFn: async () => (await api.post('/cor/import/', { country: 'RO' })).data,
    onSuccess: onImportSuccess,
    onError: (e) => toast.error(extractErr(e)),
  })

  const uploadFileMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('country', 'RO')
      fd.append('file', file)
      return (await api.post('/cor/import/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })).data
    },
    onSuccess: onImportSuccess,
    onError: (e) => toast.error(extractErr(e)),
  })

  const importing = importUrlMut.isPending || uploadFileMut.isPending

  return (
    <div className="space-y-4">
      {isRomania && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold mb-0.5">Import COR from data.gov.ro</div>
              <div className="text-xs">
                Pulls the official Romanian COR list (~4,500 6-digit codes, joint MMPS + INS publication).
                First 4 digits map to the ISCO-08 unit group — parents are created automatically.
                Safe to re-run; existing rows are updated in place.
              </div>
            </div>
            <Button
              type="button"
              onClick={() => importUrlMut.mutate()}
              disabled={importing}
            >
              {importUrlMut.isPending ? 'Importing…' : 'Import COR'}
            </Button>
          </div>
          <div className="mt-2 text-xs">
            If data.gov.ro is unreachable from the server,
            {' '}
            <button
              type="button"
              className="underline font-semibold hover:text-blue-700 disabled:opacity-50"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              upload the XML file manually
            </button>
            {' '}
            (download it once from your browser, then drop it here).
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml,application/xml,text/xml"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) uploadFileMut.mutate(file)
                // reset so the same file can be re-selected after an error
                if (e.target) e.target.value = ''
              }}
            />
          </div>
        </div>
      )}

      <PaginatedISCOTable countryId={countryId} />
    </div>
  )
}

// ──────────────────────── ISCO paginated table ────────────────────────
// Custom (not CRUDSection-backed) because the ISCO list can hold thousands
// of rows after a COR import. Fetches all rows once (cached by react-query),
// filters in-memory by search, and paginates display 50 at a time.

type ISCO = {
  id: number
  code: string
  title: string
  level: number
  description?: string
  parent?: number | null
}

const ISCO_LEVEL_LABEL = ['Major', 'Sub-major', 'Minor', 'Unit', 'Nat. 5', 'COR']
const ISCO_PAGE_SIZE = 50

function PaginatedISCOTable({ countryId }: { countryId: number }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()

  const listQ = useQuery({
    queryKey: ['isco-codes', countryId],
    queryFn: async () =>
      (await api.get<ISCO[]>('/isco-codes/', { params: { country: countryId } })).data,
  })

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [editing, setEditing] = useState<ISCO | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<any>({})

  // Filter + paginate in-memory.
  const all = listQ.data || []
  const filtered = (() => {
    const q = search.trim().toLowerCase()
    if (!q) return all
    return all.filter((r) =>
      r.code.toLowerCase().includes(q) || (r.title || '').toLowerCase().includes(q)
    )
  })()
  const pageCount = Math.max(1, Math.ceil(filtered.length / ISCO_PAGE_SIZE))
  const clampedPage = Math.min(page, pageCount - 1)
  const startIdx = clampedPage * ISCO_PAGE_SIZE
  const visible = filtered.slice(startIdx, startIdx + ISCO_PAGE_SIZE)

  // Reset to page 0 when search changes.
  function onSearchChange(v: string) {
    setSearch(v)
    setPage(0)
  }

  function openCreate() {
    setEditing(null)
    setDraft({ code: '', title: '', level: 4, description: '' })
    setCreating(true)
  }
  function openEdit(row: ISCO) {
    setCreating(false)
    setEditing(row)
    setDraft({
      code: row.code, title: row.title, level: row.level,
      description: row.description ?? '',
    })
  }
  function closeDrawer() { setCreating(false); setEditing(null) }

  const createMut = useMutation({
    mutationFn: async () =>
      (await api.post('/isco-codes/', { ...draft, country: countryId })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['isco-codes', countryId] })
      toast.success('ISCO code added'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const updateMut = useMutation({
    mutationFn: async () =>
      (await api.patch(`/isco-codes/${editing!.id}/`, draft)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['isco-codes', countryId] })
      toast.success('ISCO code saved'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/isco-codes/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['isco-codes', countryId] })
      toast.success('ISCO code deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  return (
    <section className="bg-card rounded-lg shadow">
      {dialog}

      <div className="px-6 py-3 border-b flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-foreground">ISCO-08 Code</h3>
          <p className="text-xs text-muted-foreground mt-1">
            International Standard Classification of Occupations entries scoped to this country.
            Levels 5–6 are national extensions (e.g. Romanian COR).
          </p>
        </div>
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add</Button>
      </div>

      <div className="px-6 py-3 border-b flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search by code or title…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-7 h-8 text-sm"
          />
        </div>
        <div className="text-xs text-muted-foreground whitespace-nowrap">
          {listQ.isLoading
            ? 'Loading…'
            : filtered.length === 0
              ? '0 results'
              : `Showing ${startIdx + 1}–${Math.min(startIdx + ISCO_PAGE_SIZE, filtered.length)} of ${filtered.length.toLocaleString()}${search ? ` (filtered from ${all.length.toLocaleString()})` : ''}`}
        </div>
      </div>

      <div className="p-6 pt-4">
        {listQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {search ? 'No ISCO codes match this search.' : 'No ISCO codes yet. Run the COR import (Romania) or add one manually.'}
          </p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th className="font-mono">Code</Th>
                <Th>Title</Th>
                <Th>Level</Th>
                <Th className="text-right w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visible.map((row) => (
                <tr key={row.id} className="hover:bg-muted/30">
                  <Td className="font-mono">{row.code}</Td>
                  <Td>{row.title}</Td>
                  <Td>{ISCO_LEVEL_LABEL[row.level - 1] || row.level}</Td>
                  <Td className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(row)}>Edit</DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(
                            `ISCO code ${row.code} — ${row.title}`,
                            () => deleteMut.mutate(row.id),
                          )}
                          className="text-destructive"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {filtered.length > ISCO_PAGE_SIZE && (
        <div className="px-6 py-3 border-t flex items-center justify-between text-xs text-muted-foreground">
          <div>Page {clampedPage + 1} of {pageCount.toLocaleString()}</div>
          <div className="flex items-center gap-2">
            <Button
              type="button" size="sm" variant="outline"
              disabled={clampedPage === 0}
              onClick={() => setPage(Math.max(0, clampedPage - 1))}
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </Button>
            <Button
              type="button" size="sm" variant="outline"
              disabled={clampedPage >= pageCount - 1}
              onClick={() => setPage(Math.min(pageCount - 1, clampedPage + 1))}
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      <RecordDrawer
        open={creating || editing !== null}
        onOpenChange={(v) => { if (!v) closeDrawer() }}
        title={editing ? 'Edit ISCO code' : 'New ISCO code'}
        description="ISCO-08 codes are usually imported in bulk (COR for Romania). Add or edit manually only when a code is missing or wrong."
        onSubmit={() => editing ? updateMut.mutate() : createMut.mutate()}
        isPending={createMut.isPending || updateMut.isPending}
        submitLabel={editing ? 'Save' : 'Add'}
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="isco-code">Code *</FieldLabel>
              <Input id="isco-code" required value={draft.code || ''}
                onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                className="font-mono" placeholder="e.g. 2511 or 251101" />
            </Field>
            <Field>
              <FieldLabel htmlFor="isco-level">Level</FieldLabel>
              <Select value={String(draft.level || 4)}
                onValueChange={(v) => setDraft({ ...draft, level: Number(v) })}>
                <SelectTrigger id="isco-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 — Major group</SelectItem>
                  <SelectItem value="2">2 — Sub-major group</SelectItem>
                  <SelectItem value="3">3 — Minor group</SelectItem>
                  <SelectItem value="4">4 — Unit group</SelectItem>
                  <SelectItem value="5">5 — National extension</SelectItem>
                  <SelectItem value="6">6 — National occupation (COR)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="isco-title">Title *</FieldLabel>
            <Input id="isco-title" required value={draft.title || ''}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="e.g. Systems analyst" />
          </Field>

          <Field>
            <FieldLabel htmlFor="isco-desc">Description</FieldLabel>
            <Textarea id="isco-desc" rows={3} value={draft.description || ''}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </section>
  )
}

// ──────────────────────── Night Hours (singleton-ish per country) ────────────────────────

function NightHoursTab({ countryId }: { countryId: number }) {
  const qc = useQueryClient()
  const listQ = useQuery({
    queryKey: ['night-hours', countryId],
    queryFn: async () => (await api.get('/night-hours/', { params: { country: countryId } })).data,
  })
  const existing = (listQ.data || [])[0] as any

  const [form, setForm] = useState<any>(null)
  const initialForm = existing || {
    country: countryId, start_time: '22:00', end_time: '06:00',
    premium_percent: '25', minors_prohibited: true, notes: '',
  }
  const currentForm = form ?? initialForm

  const mut = useMutation({
    mutationFn: async (payload: any) => {
      if (existing) return (await api.patch(`/night-hours/${existing.id}/`, payload)).data
      return (await api.post('/night-hours/', payload)).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['night-hours', countryId] })
      toast.success(existing ? 'Night hours updated' : 'Night hours created')
      setForm(null)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  return (
    <section className="bg-card rounded-lg shadow">
      <div className="px-6 py-3 border-b">
        <h3 className="text-base font-semibold text-foreground">Night Hours</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Statutory night-hour window for this country. Singleton — one record per country.
        </p>
      </div>

      <div className="p-6">
        {listQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); mut.mutate({ ...currentForm, country: countryId }) }}
            className="space-y-3 max-w-md"
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="start_time">Start time</Label>
                <Input id="start_time" type="time" value={currentForm.start_time || ''}
                  onChange={(e) => setForm({ ...currentForm, start_time: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="end_time">End time</Label>
                <Input id="end_time" type="time" value={currentForm.end_time || ''}
                  onChange={(e) => setForm({ ...currentForm, end_time: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="premium_percent">Night premium %</Label>
              <Input id="premium_percent" type="number" step="0.01" value={currentForm.premium_percent || ''}
                onChange={(e) => setForm({ ...currentForm, premium_percent: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!currentForm.minors_prohibited}
                onChange={(e) => setForm({ ...currentForm, minors_prohibited: e.target.checked })} />
              Workers under 18 prohibited from night work
            </label>
            <div className="space-y-1.5">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" value={currentForm.notes || ''}
                onChange={(e) => setForm({ ...currentForm, notes: e.target.value })}
                rows={2} />
            </div>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? 'Saving…' : existing ? 'Update' : 'Create'}
            </Button>
          </form>
        )}
      </div>
    </section>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-foreground ${className}`}>{children}</td>
}
